import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { ResourceList, type ResourceColumn, type ResourceFilterDef } from '@/components/resources/ResourceList';
import { useContainerActions } from '@/components/resources/useContainerActions';
import { api } from '@/lib/api';
import { formatAgo } from '@/lib/ago';
import type { ListQueryConfig } from '@/lib/listQuery';
import { useListQuery } from '@/lib/hooks/useListQuery';
import { usePersistedColumns } from '@/lib/hooks/usePersistedColumns';
import { usePolledData } from '@/lib/hooks/usePolledData';
import { useServerList } from '@/lib/hooks/useServerList';
import type { Container } from '@/types/container';

const LIST_CONFIG: ListQueryConfig = {
  filterKeys: ['instance_id', 'endpoint_id', 'status'],
  numericFilterKeys: ['instance_id', 'endpoint_id'],
  sortableColumns: ['name', 'image', 'status', 'created_at'],
  defaultSortBy: 'created_at',
  defaultSortOrder: 'desc',
};

const STATUSES: Container['status'][] = ['running', 'healthy', 'unhealthy', 'starting', 'restarting', 'paused', 'stopped', 'unknown'];
const STATUS_OPTIONS = STATUSES.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }));

const fetchInstances = () => api.instances.list();
const fetchEndpoints = () => api.endpoints.listAll();

function createdText(iso: string): { ago: string; full: string } | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return { ago: formatAgo(t), full: new Date(t).toLocaleString() };
}

const COLUMNS: ResourceColumn<Container>[] = [
  {
    key: 'name', header: 'Name', sortable: true, title: (c) => c.name,
    cell: (c) => <Link to={`/resources/containers/${c.id}`} className="text-primary font-medium hover:underline">{c.name}</Link>,
  },
  { key: 'image', header: 'Image', sortable: true, title: (c) => c.image, cell: (c) => <span className="font-mono text-xs">{c.image}</span> },
  { key: 'status', header: 'Status', sortable: true, cell: (c) => <StatusBadge status={c.status} /> },
  { key: 'instance', header: 'Instance', hideBelow: 'lg', cell: (c) => c.instance?.name ?? '—' },
  { key: 'endpoint', header: 'Endpoint', hideBelow: 'md', cell: (c) => c.endpoint?.name ?? '—' },
  { key: 'stack', header: 'Stack', hideBelow: 'lg', cell: (c) => c.stack?.name ?? '—' },
  {
    key: 'created_at', header: 'Created', sortable: true, hideBelow: 'md',
    cell: (c) => {
      const t = createdText(c.created_at);
      return t ? <time dateTime={c.created_at} title={t.full}>{t.ago}</time> : '—';
    },
  },
];
const ALL_KEYS = COLUMNS.map((c) => c.key);
const DEFAULT_KEYS = ALL_KEYS;

export function ContainersPage() {
  const { query, setFilter, setSort, setPage, setLimit, clearFilters } = useListQuery(LIST_CONFIG, { limitStorageKey: 'containers.limit' });
  const { rows, total, loading, refreshing, error, refetch } = useServerList<Container>('containers', query);
  const instancesP = usePolledData(fetchInstances);
  const endpointsP = usePolledData(fetchEndpoints);
  const [visible, toggleColumn] = usePersistedColumns('containers.columns', ALL_KEYS, DEFAULT_KEYS);
  const { actions, dialogs } = useContainerActions({ onChanged: refetch });

  const filters = useMemo<ResourceFilterDef[]>(
    () => [
      { key: 'instance_id', label: 'Instance', allLabel: 'All instances', options: (instancesP.data ?? []).map((i) => ({ value: String(i.id), label: i.name })) },
      { key: 'endpoint_id', label: 'Endpoint', allLabel: 'All endpoints', options: (endpointsP.data ?? []).map((e) => ({ value: String(e.id), label: e.name })) },
      { key: 'status', label: 'Status', allLabel: 'All statuses', options: STATUS_OPTIONS },
    ],
    [instancesP.data, endpointsP.data],
  );

  const onPage = useCallback((p: number) => setPage(p, total), [setPage, total]);

  return (
    <div className="space-y-6">
      <PageHeader title="Containers" description="Containers synced from your Portainer instances." />
      <ResourceList<Container>
        columns={COLUMNS}
        visibleColumns={visible}
        onToggleColumn={toggleColumn}
        rows={rows}
        total={total}
        getRowKey={(c) => c.id}
        query={query}
        onFilter={setFilter}
        onSort={setSort}
        onPage={onPage}
        onLimit={setLimit}
        onClearFilters={clearFilters}
        filters={filters}
        loading={loading}
        refreshing={refreshing}
        error={error}
        onRetry={refetch}
        tableLabel="Containers"
        rowActions={actions}
        empty={{ title: 'No containers yet', description: 'Containers appear here after an instance syncs.' }}
      />
      {dialogs}
    </div>
  );
}
